import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { key } from 'styled-theme';

import { T } from 'containers';
import { AppText } from 'domain/appText';

import GlowHeading from '../../../components/Atoms/GlowHeading';
import GuestInCabin from '../GuestInCabin';

export const bgColors = {
  empty: '#fff',
  complete: '#e1f0e7',
  started: '#fff',
  selected: '#fff2cc',
  error: '#fdd',
};

export const borderColors = {
  empty: '#e7e7e7',
  complete: '#a3ccb4',
  started: '#e7e7e7',
  selected: '#ffdb70',
  white: '#fff',
  error: '#e5bcbc',
};

export const fontColor = {
  empty: '#404040',
  complete: '#4d8062',
  started: '#404040',
  selected: '#403000',
  error: '#804040',
};


const Marker = styled.span`
  width: 28px;
  height: 28px;
  line-height: ${ifProp('selected', '28px', '28px')};
  text-align: center;
  color: white;
  font-weight: bold;
  border-radius: 100%;
  margin-right: 20px;
  position: relative;
  display: inline-block;
  vertical-align: middle;
  transition: all ${key('duration')};
  color: ${ifProp('selected', fontColor.selected, '#fff')};
  background-color: ${ifProp('selected', 'transparent', '#404040')};
  ${ifProp('isFull', 'background-color: #2dcb70', '')};
  font-size: ${ifProp('selected', '1.8em', '1em')};
  overflow: hidden;
  text-indent: ${ifProp('isFull', '-9999px', '0')};

  img{
    display: none;
    display: ${ifProp('isFull', 'block', 'none')};
    position: absolute;
    left: 0;
    top: 0;
  }
`;
const Capacity = styled.div`
  display: block;
  position: absolute;
  font-size: 12px;
  height: 20px;
  line-height: 20px;
  right: 20px;
  top: 50%;
  margin-top: -10px;
  opacity: 0.3;
  color: ${ifProp('selected', fontColor.selected, fontColor.started)};
`;
const StyledGuestInCabin = styled(GuestInCabin)`
  margin: 5px 5px 0px 0;
  line-height: 18px;
`;
const Content = styled.div`
  display: inline-block;
  vertical-align: middle;
  width: calc(100% - 50px);

  .passengers{
    font-size: 0.7em;
    line-height: 1em;
    margin-right: 100px;

    span.no-passangers{
      opacity: 0.33;
      margin-top: 5px;
    }
  }
`;
const ListItem = styled.li`
  list-style: none;
  margin: 0;
  border-bottom: 1px solid #dae6f2;
  margin-top: -1px;
  padding: 30px 20px;
  position: relative;
  transition: all ${key('duration')};
  cursor: pointer;

  &:hover{
    z-index: 1;
  }

  ${StyledGuestInCabin} {
    border-color: ${ifProp('selected', fontColor.selected, fontColor.started)};
  }

  ${({ isFull, isOverfull, isStarted, selected }) => {
    let bg;
    let bCol;
    let fCol;
    let zind;
    let blrColor;
    if (selected) {
      bg = bgColors.selected;
      bCol = borderColors.selected;
      fCol = fontColor.selected;
      blrColor = borderColors.selected;
      zind = 1;
    } else if (isFull) {
      bg = bgColors.complete;
      bCol = borderColors.complete;
      blrColor = borderColors.complete;
      fCol = fontColor.complete;
      zind = 0;
    } else if (isOverfull) {
      bg = bgColors.error;
      bCol = borderColors.error;
      fCol = fontColor.error;
      blrColor = borderColors.error;
      zind = 0;
    } else if (isStarted) {
      bg = bgColors.started;
      bCol = borderColors.started;
      fCol = fontColor.started;
      blrColor = borderColors.white;
      zind = 0;
    } else {
      bg = bgColors.started;
      bCol = borderColors.started;
      fCol = fontColor.started;
      blrColor = borderColors.white;
      zind = 0;
    }

    const listItemCss = isFull ? css`
      {
        border-left: none;
        border-right: none;
        cursor: default !important;
      }
    ` : '';
    const marginCss = isFull ? css`
      ${Content} .passengers {
        margin-right: 0;
      }
    ` : '';
    const selecedCss = selected ? css`
      color: ${fCol};
      ${StyledGuestInCabin} {
        svg{
          fill: ${fCol};
          stroke: ${fCol};
        }
      }
    ` : css`
      ${StyledGuestInCabin} {
        border-color: ${fCol};
        color: ${fCol};
        svg{
          fill: ${fCol};
          stroke: ${fCol};
        }
      }
      &:hover {
        border-color: ${fCol};
        cursor: pointer;
      }
    `;

    return css`
      background-color: ${bg};
      border: 1px solid ${bCol};
      border-right-color: ${blrColor};
      border-left-color: ${blrColor};
      color: ${fCol};
      z-index: ${zind};
      ${selecedCss}
      ${marginCss}
      ${listItemCss}
    `;
  }}
`;
const CabinHeading = styled(GlowHeading)`
  margin: 0;
  font-size: 1rem;
  padding-right: 25%;
`;

class Cabin extends React.Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    size: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    guests: PropTypes.array,
    number: PropTypes.number,
    noOfBerths: PropTypes.number,
    selected: PropTypes.bool,
    onClick: PropTypes.func,
    onRemoveGuest: PropTypes.func,
    remainingBeds: PropTypes.number,
    cabinsAllocated: PropTypes.bool,
  };
  static defaultProps = {
    size: 0,
    guests: [],
    onClick: () => {},
    onRemoveGuest: () => {},
  };

  onRemoveGuest = (guestId) => {
    const { cabinsAllocated, remainingBeds } = this.props;
    if (!cabinsAllocated) {
      this.props.onRemoveGuest(guestId, this.props.id);
      if (remainingBeds === 0) this.props.onClick();
    }
  }

  onClick = () => {
    const { remainingBeds } = this.props;
    if (remainingBeds > 0) this.props.onClick();
  }

  render() {
    const { title, description, guests, number, selected, remainingBeds, noOfBerths } = this.props;
    const beds = noOfBerths - guests.length;
    return (
      <T
        text={[
          AppText.NoGuestsInCabin,
          AppText.OneBedLeft,
          AppText.MultipleBedsLeft,
        ]}
        render={([
          noGuestsInCabin,
          oneBedLeft,
          multipleBedsLeft,
        ]) => (
          <ListItem selected={selected} onClick={this.onClick} isFull={remainingBeds === 0} isOverfull={remainingBeds < 0} isStarted={guests.length} >
            <Marker selected={selected} isFull={remainingBeds === 0}>{number}<img src="/assets/images/ok.svg" alt="Done" /></Marker>
            <Content className="inner-content">
              <CabinHeading level={4}>{title || description}</CabinHeading>
              <div className="passengers">
                {guests && guests.map(id => <StyledGuestInCabin key={id} id={id} onRemoveGuest={this.onRemoveGuest} />)}
                {(!guests || guests.length === 0) && <span className="no-passangers">{noGuestsInCabin}</span>}
              </div>
            </Content>
            {beds > 0 && <Capacity selected={selected}>{beds} {beds === 1 ? oneBedLeft : multipleBedsLeft}</Capacity>}
          </ListItem>
        )}
      />
    );
  }
}


export default Cabin;



// WEBPACK FOOTER //
// ./src/components/molecules/Cabin/index.js